/**
 * @file 資料のさくいん (語 → ページ) の並べ方と検索 (純関数)。
 *
 * @what: 語の五十音順 (同じ語はページ順) に並べる、部分一致で探す (正規化した語で比べ、
 *   前方一致を先に)、上限で切る。
 * @why: さくいんは資料ごとに数百〜千語で、client の配列で足りる。西洋美術解読事典の
 *   見出し語さくいん (補助ページ) の探し方を、索引を持つ全ての資料に広げた。
 * @failure: 副作用を持たない。空の問いは全ての語を五十音順で返す (面はページ送りで出す)。
 */

import type { StudySourceIndexEntry } from "../api/sourceIndex";
import { normalizeTerm } from "./sourceLinks";

/** 語の五十音順 (同じ語はページ順)。 */
export function sortIndexEntries(
  entries: readonly StudySourceIndexEntry[],
): StudySourceIndexEntry[] {
  return [...entries].sort((a, b) => a.term.localeCompare(b.term, "ja") || a.page - b.page);
}

/**
 * 語で探す。前方一致 → 部分一致 の順、それぞれ五十音順。
 *
 * @what: 比べるときは正規化 (全角半角・空白・記号を落とす) し、「受胎 告知」でも
 *   「受胎告知」に当たる。空の問いは全ての語を五十音順で返す。
 */
export function searchIndexEntries(
  entries: readonly StudySourceIndexEntry[],
  query: string,
  limit = Number.POSITIVE_INFINITY,
): StudySourceIndexEntry[] {
  const q = normalizeTerm(query);
  const sorted = sortIndexEntries(entries);
  if (q === "") return sorted.slice(0, limit);
  const starts: StudySourceIndexEntry[] = [];
  const contains: StudySourceIndexEntry[] = [];
  for (const e of sorted) {
    const t = normalizeTerm(e.term);
    if (t.startsWith(q)) starts.push(e);
    else if (t.includes(q)) contains.push(e);
  }
  return [...starts, ...contains].slice(0, limit);
}

/** 語の頭文字 (五十音の行の見出しなどに使う。英数字はそのまま)。 */
export function firstChar(term: string): string {
  return [...term.trim()][0] ?? "";
}
